import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ZoomInIcon, XIcon } from 'lucide-react';
import { useBackNavigation } from '../hooks/useBackNavigation';
export function CoLiving() {
  const goBack = useBackNavigation('/work');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  return <div className="w-full min-h-screen bg-white" data-id="element-1160">
      {/* Lightbox */}
      {selectedImage && <div className="fixed inset-0 z-50 bg-stone-900/95 flex items-center justify-center p-6" onClick={() => setSelectedImage(null)} data-id="element-1161">
          <button className="absolute top-6 right-6 text-white hover:text-stone-300 transition-colors" onClick={() => setSelectedImage(null)} data-id="element-1162">
            <XIcon className="w-8 h-8" data-id="element-1163" />
          </button>
          <img src={selectedImage} alt="Enlarged view" className="max-w-full max-h-full object-contain" onClick={e => e.stopPropagation()} data-id="element-1164" />
        </div>}
      {/* Header */}
      <header className="py-4 px-6 bg-white border-b border-stone-200 sticky top-0 z-40" data-id="element-1165">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-1166">
          <button onClick={goBack} className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 transition-colors group" data-id="element-1167">
            <ArrowLeftIcon className="w-5 h-5 group-hover:-translate-x-1 transition-transform" data-id="element-1168" />
            <span className="font-light" data-id="element-1169">Back</span>
          </button>
          <h1 className="text-2xl font-light text-stone-900" data-id="element-1170">Maria Elina</h1>
        </div>
      </header>
      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-stone-50 via-emerald-50/30 to-stone-50" data-id="element-1171">
        <div className="max-w-5xl mx-auto" data-id="element-1172">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-1173">
            <p className="text-emerald-700 font-light tracking-wide uppercase mb-4" data-id="element-1174">
              Interior Architecture • Adaptive Re-Use
            </p>
            <h1 className="text-4xl md:text-6xl font-light text-stone-900 mb-6" data-id="element-1175">
              Co-Living
            </h1>
            <p className="text-xl text-stone-600 font-light mb-8" data-id="element-1176">
              Transforming an underutilized office building into affordable,
              community-focused living spaces
            </p>
            <div className="space-y-4 text-stone-700 font-light" data-id="element-1177">
              <p className="text-lg" data-id="element-1178">
                <span className="font-normal text-stone-900" data-id="element-1179">Role:</span>{' '}
                Interior Architect, Spatial Designer
              </p>
              <p className="text-lg" data-id="element-1180">
                <span className="font-normal text-stone-900" data-id="element-1181">Location:</span>{' '}
                Dean Park Crescent, Bournemouth
              </p>
              <p className="text-lg" data-id="element-1182">
                <span className="font-normal text-stone-900" data-id="element-1183">Type:</span>{' '}
                Academic Project, Adaptive Reuse, Community Design
              </p>
              <p className="text-lg" data-id="element-1184">
                <span className="font-normal text-stone-900" data-id="element-1185">
                  Design Philosophy:
                </span>{' '}
                Eclectic and Bohemian embracing reduce-reuse-recycle ideology
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Floor Plans */}
      <section className="py-20 px-6 bg-white" data-id="element-1186">
        <div className="max-w-7xl mx-auto" data-id="element-1187">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1188">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1189">
              Building Organization
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1190">
              The adaptive reuse design maximizes the existing building
              structure, creating distinct zones for living, working, and
              community spaces across multiple floors.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1191">
              {/* First Image */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(35).png')} data-id="element-1192">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1193">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(35).png" alt="Floor Plans Overview" className="w-full h-auto" data-id="element-1194" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1195">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1196" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1197">
                  Axonometric floor plan views showing living units and communal
                  spaces
                </p>
              </motion.div>
              {/* Second Image */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(34).png')} data-id="element-1198">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1199">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(34).png" alt="Exterior View" className="w-full h-auto" data-id="element-1200" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1201">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1202" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1203">
                  Exterior view showing entrance ideation
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Building Study */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1204">
        <div className="max-w-7xl mx-auto" data-id="element-1205">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1206">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1207">
              Building Study
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1208">
              The office structure facilitates 4 distinct zones visible through
              building sections - regular living apartments (floors G to 5),
              suites (floor 6), entertainment basement and a social rooftop.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1209">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/sections.png')} data-id="element-1210">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1211">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/sections.png" alt="Full Building Sections" className="w-full h-auto" data-id="element-1212" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1213">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1214" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1215">
                  Full building sections
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/Section%20(1).png')} data-id="element-1216">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1217">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/Section%20(1).png" alt="Detailed Building Sections" className="w-full h-auto" data-id="element-1218" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1219">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1220" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1221">
                  Detailed sections showing the basement zones
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Design Philosophy */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1222">
        <div className="max-w-5xl mx-auto" data-id="element-1223">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1224">
            <h2 className="text-3xl font-light text-stone-900 mb-6" data-id="element-1225">
              Trendy yet Timeless
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed mb-12" data-id="element-1226">
              <p data-id="element-1227">
                Eclectic and Bohemian styles were chosen to create spaces that
                embrace a mixture of furnishing styles, perfectly aligning with
                the reduce-reuse-recycle ideology. This approach celebrates
                sustainability while creating warm, inviting environments.
              </p>
              <p data-id="element-1228">
                The design creates a new model of living that brings together
                residents with similar aspirations into one communal family. It
                is aimed at active residents in remote employment who want to
                live in central Bournemouth without compromising comfort or
                finances.
              </p>
            </div>
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(22).png')} data-id="element-1229">
              <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1230">
                <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(22).png" alt="Material Studies and Design Aesthetic" className="w-full h-auto" data-id="element-1231" />
                <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1232">
                  <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1233" />
                </div>
              </div>
              <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1234">
                Material studies showcasing natural materials, repurposed
                furniture, and the eclectic bohemian aesthetic
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
      {/* Working Spaces */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1235">
        <div className="max-w-7xl mx-auto" data-id="element-1236">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1237">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1238">
              Working and Enjoying
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1239">
              Each living floor is equipped with dedicated working spaces and
              innovative "working pods" that ascend between floors, providing
              flexible environments for remote professionals.
            </p>
            <div className="grid grid-cols-1 gap-6 max-w-4xl mx-auto" data-id="element-1240">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(21).png')} data-id="element-1241">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1242">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(21).png" alt="Working Pods" className="w-full h-auto" data-id="element-1243" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1244">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1245" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1246">
                  Working pods integrated with greenery, creating inspiring
                  spaces between floors
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/interior%20final%20projection%20living%20room.png')} data-id="element-1247">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1248">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/interior%20final%20projection%20living%20room.png" alt="Rooftop Space" className="w-full h-auto" data-id="element-1249" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1250">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1251" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1252">
                  Residential floors combining work and social areas
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Basement Oasis */}
      <section className="py-20 px-6 bg-white" data-id="element-1253">
        <div className="max-w-7xl mx-auto" data-id="element-1254">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1255">
            <h2 className="text-3xl font-light text-stone-900 mb-4" data-id="element-1256">
              The Basement Oasis
            </h2>
            <p className="text-lg text-stone-600 font-light mb-12 max-w-3xl" data-id="element-1257">
              A unique feature made possible by the daylight re-directing
              technology, allowing natural sunlight to reach the plants below
              and create a lush, green communal space.
            </p>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1258">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(38).png')} data-id="element-1259">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1260">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/image%20(38).png" alt="Basement Corridor" className="w-full h-auto" data-id="element-1261" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1262">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1263" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1264">
                  Corridor surrounding the oasis leading to entertainment areas
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="group cursor-pointer" onClick={() => setSelectedImage('https://storage.yandexcloud.net/maria-images/images/co-living_ready/cafe%20basement%20view%203d.png')} data-id="element-1265">
                <div className="relative overflow-hidden rounded-2xl shadow-xl" data-id="element-1266">
                  <img src="https://storage.yandexcloud.net/maria-images/images/co-living_ready/cafe%20basement%20view%203d.png" alt="Basement Cafe" className="w-full h-auto" data-id="element-1267" />
                  <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300 flex items-center justify-center" data-id="element-1268">
                    <ZoomInIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-1269" />
                  </div>
                </div>
                <p className="text-stone-600 font-light mt-4 text-center italic" data-id="element-1270">
                  The basement cafe featuring green decor and mixed upcycled
                  furniture
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Project Impact */}
      <section className="py-20 px-6 bg-stone-50" data-id="element-1271">
        <div className="max-w-5xl mx-auto" data-id="element-1272">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1273">
            <h2 className="text-3xl font-light text-stone-900 mb-8" data-id="element-1274">
              A New Model of Living
            </h2>
            <div className="space-y-6 text-lg text-stone-600 font-light leading-relaxed" data-id="element-1275">
              <p data-id="element-1276">
                This co-living project addresses Bournemouth's lack of
                affordable housing for young professionals while championing
                sustainable design practices through material reuse and adaptive
                building transformation.
              </p>
              <p data-id="element-1277">
                By integrating nature throughout the building - from the
                basement oasis to rooftop gardens - the design creates a
                holistic living environment that supports both productivity and
                well-being, fostering a genuine sense of community among
                residents.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}